import { useMemo } from 'react';
import sportFutbol from '../../../../assets/sport_futbol.png';
import sportBaloncesto from '../../../../assets/sport_basquetbol.png';
import sportFutbolAmericano from '../../../../assets/sport_americano.png';
import sportBeisbol from '../../../../assets/sport_beisbol.png';
import sportFormula1 from '../../../../assets/sport_motor.png';
import sportTenis from '../../../../assets/sport_tenis.png';
import './sportFilter.sass';

/**
 * Componente de filtrado por deporte
 * Permite filtrar el contenido mostrado por tipo de deporte
 * Muestra todos los deportes pero desactiva los que no tienen datos
 * Si Fútbol no está disponible, activa automáticamente el siguiente deporte
 * 
 * @param {Object} props
 * @param {Array} props.tabsData - Datos de todas las tabs
 * @param {number} props.currentTab - Tab actualmente activa
 * @param {string} props.selectedSport - Código del deporte seleccionado
 * @param {Function} props.onSportChange - Callback cuando cambia el deporte
 * @param {boolean} props.hasLiveGames - Si hay juegos en vivo en el primer render
 */
export default function SportFilter({ selectedSport, onSportChange, tabsData, currentTab, isViewingToday }) {
  // Mapeo de deportes disponibles con sus códigos
  const SPORTS_MAP = {
    FOOT: { name: 'Fútbol', icon: sportFutbol, sport_code: 'FOOT' },
    BASK: { name: 'Baloncesto', icon: sportBaloncesto, sport_code: 'BASK' },
    AMFO: { name: 'Fútbol Americano', icon: sportFutbolAmericano, sport_code: 'AMFO' },
    BASE: { name: 'Béisbol', icon: sportBeisbol, sport_code: 'BASE' },
    MTR: { name: 'Fórmula 1', icon: sportFormula1, sport_code: 'MTR' },
    TNS: { name: 'Tenis', icon: sportTenis, sport_code: 'TNS' }
  };

  // Orden de deportes para fallback
  const SPORTS_ORDER = ['FOOT', 'BASK', 'AMFO', 'BASE', 'MTR', 'TNS'];

  // Contar eventos en vivo por deporte
  const countLiveGamesBySport = (sportCode) => {
    if (!tabsData || !tabsData[currentTab - 1]?.data?.sports) return 0;
    
    const sports = tabsData[currentTab - 1].data.sports;
    const sport = sports.find(s => s.sport_code === sportCode);
    
    if (!sport || !sport.leagues) return 0;
    
    let count = 0;
    sport.leagues.forEach(league => {
      if (league.games && Array.isArray(league.games)) {
        count += league.games.filter(game => game.class === 'clarosports-in-progress-game').length;
      }
    });
    
    return count;
  };

  // Obtener lista de todos los deportes
  const allSports = useMemo(() => {
    // Solo calcular si estamos viendo HOY
    if (!isViewingToday) {
      return SPORTS_ORDER.map(sportKey => {
        const sportConfig = SPORTS_MAP[sportKey];
        return {
          key: sportKey,
          code: sportConfig.sport_code,
          name: sportConfig.name,
          icon: sportConfig.icon,
          liveCount: 0
        };
      });
    }

    // Crear lista de todos los deportes
    const allSportsList = SPORTS_ORDER.map(sportKey => {
      const sportConfig = SPORTS_MAP[sportKey];

      return {
        key: sportKey,
        code: sportConfig.sport_code,
        name: sportConfig.name,
        icon: sportConfig.icon,
        liveCount: countLiveGamesBySport(sportConfig.sport_code)
      };
    }).filter(Boolean);

    return allSportsList;
  }, [isViewingToday, tabsData, currentTab]);

  // Si no hay deportes, no renderizar
  if (allSports.length === 0) {
    return null;
  }

  return (
    <div className="sport-filter-container">
      <div className="sport-filter-buttons">
        {allSports.map(sport => (
          <button
            key={sport.code}
            className={`sport-filter-btn ${selectedSport === sport.code ? 'active' : 'inactive'}`}
            onClick={() => onSportChange(sport.code)}
            aria-label={`Filtrar por ${sport.name}`}
            title={sport.name}
          >
            <img 
              src={sport.icon} 
              alt={sport.name}
              className="sport-icon"
            />
            {sport.liveCount > 0 && (
              <span className="sport-filter-badge">{sport.liveCount}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
